import React from 'react';
import { datetime } from '@common/services/datetime';
import { Row, Col } from 'antd';
import { IncentiveRatesView } from '@scheduling/components';
import { Gutter, Icon, Text, Title } from '@common/components';
import { useCurrentMembership } from '@identity/hooks';
import { UnitRateType } from '@scheduling/graphql';
import {
  toHumanDateTimeWithTZ,
  toUSFormat,
} from 'works/legacy/lib/date-helper';

function DynamicRateDetails({ unitRates }: { unitRates: UnitRateType }) {
  return (
    <>
      <Col span={24}>
        <Gutter size="md" direction="vertical">
          <IncentiveRatesView.Dynamic rates={unitRates} />
        </Gutter>
      </Col>
      <Col span={24}>
        <IncentiveRatesView.Variable rates={unitRates} />
      </Col>
    </>
  );
}

function StaticRateDetails({ unitRates }: { unitRates: UnitRateType }) {
  if (!unitRates.staticPricingTiers?.length) return null;

  return (
    <Col span={24}>
      <IncentiveRatesView.Static
        tiers={unitRates.staticPricingTiers}
        displayAsLumpSum={unitRates.displayAsLumpSum}
      />
    </Col>
  );
}

export function IncentiveRatesViewDrawerUnitRatesRow({
  unitRates,
}: {
  unitRates: UnitRateType;
}) {
  const { effectiveDate, endDate, createdAt, updatedAt, createdBy } = unitRates;
  const isStatic = unitRates.pricingMode === 'STATIC';
  const currentMembership = useCurrentMembership({ hideError: true });
  const timeZone = currentMembership?.worksUser?.timeZone;

  const startDateEqualsEndDate = datetime(effectiveDate).isSame(endDate);
  const adjustedEndDate = startDateEqualsEndDate
    ? datetime(endDate).endOf('day')
    : datetime(endDate).subtract(1, 'days').endOf('day');
  const adjustedEffectiveDate = datetime(effectiveDate).startOf('day');

  return (
    <Row gutter={[0, 16]}>
      <Col span={24}>
        <Row gutter={[0, 8]}>
          <Col span={5}>
            <Title size="h3">
              {toUSFormat(adjustedEffectiveDate || updatedAt || createdAt)}
              <Gutter inlineBlock direction="left">
                <Icon icon="sun" color="harvestGold" size="lg" />
              </Gutter>
            </Title>
          </Col>
          {endDate && (
            <Col span={19}>
              <Title size="h3">
                {`- ${toUSFormat(adjustedEndDate)}`}
                <Gutter inlineBlock direction="left">
                  <Icon icon="moon" color="skyRegular" size="lg" />
                </Gutter>
              </Title>
            </Col>
          )}
        </Row>
      </Col>
      {isStatic ? (
        <StaticRateDetails unitRates={unitRates} />
      ) : (
        <DynamicRateDetails unitRates={unitRates} />
      )}
      {createdBy && (
        <Col span={24}>
          <Text className="d-block">
            {createdBy.name} made these changes on{' '}
            {toHumanDateTimeWithTZ(createdAt, timeZone)}.
          </Text>
        </Col>
      )}
    </Row>
  );
}
